import { useGet, usePut } from "@/apiHooks/HttpFunctions";
import { EditButton } from "@/components/IconButton/variants";
import CancelButton from "@/components/IconButton/variants/CancelButton";
import ChecklistButton from "@/components/IconButton/variants/ChecklistButton";
import Input from "@/components/Input/Input";
import BaseExport from "@/components/NewTable/BaseExport";
import BaseImport from "@/components/NewTable/BaseImport";
import useHttpErrorToast from "@/CustomHooks/useHttpErrorToast";
import BaseImportModal from "@/Modals/ShippingManagement/BaseImport/wrapper";
import { ArrowDown, ArrowRight } from "lucide-react";
import { useState, useEffect, useMemo, useRef } from "react";

function ShippingManagementBaseTable({ apiURL, label }) {
  const modalImportRef = useRef(null);
  const { data: response, error, isPending, refetch } = useGet({ apiURL });

  const { mutate: saveEdit, error: saveError } = usePut({
    apiURL,
  });

  const [data, setData] = useState([]);

  const headers = useMemo(() => {
    const headerSet = new Set();
    data.forEach((item) => {
      const countryCode = Object.keys(item).find((key) => key !== "fullname");
      if (countryCode) {
        Object.keys(item[countryCode]).forEach((code) => {
          headerSet.add(code);
        });
      }
    });
    return Array.from(headerSet);
  }, [data]);

  useEffect(() => {
    if (response?.data) {
      setData(response.data);
    }
  }, [response?.data]);

  const [editingRow, setEditingRow] = useState(null);
  const [editValues, setEditValues] = useState({});

  const handleEditClick = (countryCode) => {
    setEditingRow(countryCode);
    const initialValues = {};
    const rowData = data.find(
      (item) =>
        Object.keys(item).find((key) => key !== "fullname") === countryCode
    );
    if (rowData) {
      const rates = rowData[countryCode];
      headers.forEach((header) => {
        initialValues[header] = rates[header]?.toString() || "";
      });
    }
    setEditValues(initialValues);
  };

  const handleSave = () => {
    if (editingRow) {
      setData((prev) =>
        prev.map((item) => {
          const countryCode = Object.keys(item).find(
            (key) => key !== "fullname"
          );
          if (countryCode === editingRow) {
            const newRates = {};
            headers.forEach((header) => {
              newRates[header] =
                editValues[header] === "" ? null : Number(editValues[header]);
            });
            const output = Object.entries(newRates)
              .filter(([_, value]) => value !== null)
              .map(([key, value]) => ({
                origin: editingRow,
                destination: key,
                value,
              }));

            saveEdit(
              {
                matrix: output,
              },
              {
                onSuccess: () => {
                  setEditingRow(null);
                  setEditValues({});
                },
              }
            );
            return {
              ...item,
              [countryCode]: newRates,
            };
          }
          return item;
        })
      );

      setEditingRow(null);
      setEditValues({});
    }
  };

  const handleCancel = () => {
    setEditingRow(null);
    setEditValues({});
  };

  const handleInputChange = (col, value) => {
    setEditValues((prev) => ({
      ...prev,
      [col]: value,
    }));
  };

  useHttpErrorToast(saveError);

  if (isPending) {
    return (
      <div className="flex justify-center items-center h-full">Loading...</div>
    );
  }

  if (error) {
    return <div className="text-red-500 text-center">Error fetching data.</div>;
  }
  return (
    <>
      <BaseImportModal
        dataName="Shipping Matrix Standard"
        ref={modalImportRef}
        apiURL={apiURL}
        refetch={refetch}
      />
      <div className="h-full flex flex-col gap-y-4">
        {/* EXPORT IMPORT */}
        <div className="w-full flex gap-x-2 justify-end items-center">
          <BaseImport
            data={{
              onClick: () => {
                modalImportRef.current.open();
              },
            }}
          />
          <BaseExport
            exportData={{
              api: apiURL,
              label,
            }}
          />
        </div>

        {/* Table Container */}
        <div className="flex-1 border border-black/50 font-light text-sm bg-white overflow-auto max-h-full">
          <table className="w-full min-w-max border-collapse">
            <thead>
              <tr>
                <th
                  className="outline outline-1 outline-black/50 bg-white px-2 py-2 sticky top-0 left-0 z-40"
                  rowSpan={2}
                ></th>
                <th
                  className="outline outline-1 outline-black/50 bg-white px-2 w-36 py-2 sticky top-0 left-16 z-40"
                  rowSpan={2}
                ></th>
                <th className="outline outline-1 outline-black/50 bg-white px-2 py-1 sticky top-0 left-52 z-40">
                  <div className="flex items-center gap-x-2 justify-center">
                    <p>Destination</p> <ArrowRight size={18} />
                  </div>
                </th>
                {headers.map((country) => (
                  <th
                    key={country}
                    className="outline outline-1 outline-black/50 px-2 py-2 sticky top-0 z-30 bg-white"
                    rowSpan={2}
                  >
                    {country}
                  </th>
                ))}
              </tr>
              <tr>
                <th className="outline outline-1 outline-black/50 px-2 py-1 bg-white sticky top-[28px] left-52 z-40">
                  <div className="flex gap-x-2 items-center justify-center">
                    <p>Origin</p>
                    <ArrowDown size={18} />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.map((row) => {
                const countryCode = Object.keys(row).find(
                  (key) => key !== "fullname"
                );
                if (!countryCode) return null;
                const rates = row[countryCode];
                return (
                  <tr key={countryCode}>
                    <td className="outline outline-1 outline-black/50 w-16 py-2 sticky left-0 bg-white z-[35]">
                      <div className="flex items-center gap-x-2 justify-center">
                        {editingRow === countryCode ? (
                          <div className="flex gap-x-1">
                            <CancelButton onClick={handleCancel} />
                            <ChecklistButton onClick={handleSave} />
                          </div>
                        ) : (
                          <EditButton
                            onClick={() => handleEditClick(countryCode)}
                          />
                        )}
                      </div>
                    </td>
                    <td className="outline text-center outline-1 outline-black/50 w-36 py-2 sticky left-16 bg-white z-30">
                      {row?.fullname}
                    </td>
                    <td className="outline outline-1 outline-black/50 text-center font-semibold px-2 py-2 sticky left-52 bg-white z-30">
                      {countryCode}
                    </td>
                    {headers.map((header) => (
                      <td
                        key={`${countryCode}-${header}`}
                        className="border border-black/40 px-2 py-2 text-center"
                      >
                        {editingRow === countryCode ? (
                          <Input
                            inputBoxDiv="!h-5"
                            type="number"
                            value={editValues[header] || ""}
                            min="0"
                            onChange={(e) =>
                              handleInputChange(header, e.target.value)
                            }
                            className="w-8 text-center"
                          />
                        ) : (
                          <span>{rates[header] ?? "-"}</span>
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

export default ShippingManagementBaseTable;
